'use client';

import { useCallback, useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { PanelLeftOpen } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { useResizableColumn } from '@/shared/lib/hooks/use-resizable-column';

interface DesktopShellProps {
  sidebar: ReactNode;
  children: ReactNode;
  /**
   * Render the brand teal hero wash full-bleed behind BOTH columns (project
   * home only). The frosted-glass sidebar then blurs the teal beneath it, so
   * the wash reads as one continuous gradient flowing under the navigation —
   * mirroring Apple's `backgroundExtensionEffect`. On the home page the page's
   * own in-content wash (`ProjectHeroSection`) is hidden at `lg+` so this is
   * the single source; below `lg` the shell/sidebar don't exist and the page
   * wash takes over.
   */
  /**
   * Mobile passthrough (< lg). Renders the SAME wrapper element tree but with
   * `display: contents`, so the boxes vanish (visually identical to rendering
   * `children` bare) WHILE keeping `children` at a stable position in the React
   * tree across the breakpoint. Without this, crossing `lg` swapped the tree
   * shape and React unmounted+remounted the whole content subtree on resize —
   * destroying any in-flight local state (e.g. the angle upload/validation
   * modal). The sidebar and hero wash are skipped in this mode.
   */
  bare?: boolean;
}

/**
 * Expanded sidebar width on a laptop-sized window, and the narrowest it goes.
 * Narrowed from 213 to 186 (-13%) so the content column gets the space back on
 * the tightest desktop-layout viewports (iPad-class widths, where the shell
 * first switches to the two-column layout). 186 still fits the longest nav
 * labels across the 24 locales at the current type scale. Kept in sync with the
 * Apple app's `LayoutMode.sidebarWidth`.
 */
const SIDEBAR_WIDTH_EXPANDED = 186;

/** Wider than this the sidebar is no longer a nav rail but a second column. */
const SIDEBAR_MAX_WIDTH = 360;
const SIDEBAR_STORAGE_KEY = 'sidebar-width';

/**
 * Sidebar width this window picks on its own, before the user drags its edge.
 * A large display can afford a roomier nav without taking anything the content
 * misses; a laptop keeps 186. Mirrored in Apple's
 * `LayoutMode.defaultSidebarWidth`.
 */
function defaultSidebarWidthFor(windowWidth: number): number {
  if (windowWidth >= 1920) return 240;
  if (windowWidth >= 1600) return 220;
  return SIDEBAR_WIDTH_EXPANDED;
}

/**
 * Leading gutter reserved on the content column while the sidebar is collapsed,
 * so the page's leading content clears the floating sidebar-reveal button in the
 * leading top corner (see the button in the shell body). Must stay in sync with
 * the Tailwind `ps-14` applied to the content row (14 × 4px = 56px).
 */
const COLLAPSED_LEADING_GUTTER = 56;

/**
 * Inline distance (px) from the viewport's leading edge to the content column's
 * leading edge while the sidebar is shown: sidebar `ms-4` (16) + width + `gap-4`
 * (16). Used by body-portaled, viewport-fixed overlays (the stacked settings
 * drill-down) that can't inherit the `--content-inset-start` CSS var to align
 * with the content column instead of covering the sidebar. Applied via
 * `inset-inline-start`, so it flips with the document direction (RTL puts the
 * sidebar on the right).
 */
export const CONTENT_COLUMN_INLINE_START = 16 + SIDEBAR_WIDTH_EXPANDED + 16;

/**
 * The same distance as a CSS variable, published on the document element so
 * BODY-portaled overlays can read it. They cannot inherit the shell's own
 * variables, and the constant above is only right while the sidebar sits at its
 * default width — a resized sidebar would leave those overlays covering it or
 * floating away from the content column.
 */
export const CONTENT_COLUMN_INLINE_START_VAR = '--content-column-inline-start';

/**
 * The trailing counterpart: what a docked column on the trailing side (the AI
 * architect on a wide window) takes from the content column. Published by that
 * column while it is docked, absent otherwise, so body-portaled overlays stop
 * at it instead of sliding underneath.
 */
export const CONTENT_COLUMN_INLINE_END_VAR = '--content-column-inline-end';

/**
 * Trailing padding for a page's own scroll container: at least `minPx` (the
 * collapsed-sidebar re-centering gutter), and at least what a docked column on
 * the trailing side takes. It lives INSIDE the scroller (padding is within the
 * border box) so the scrollbar stays at the window's edge instead of the
 * docked column's.
 */
export function contentColumnEndPadding(minPx = 0): CSSProperties {
  return {
    paddingInlineEnd: `max(${minPx}px, var(${CONTENT_COLUMN_INLINE_END_VAR}, 0px))`,
  };
}

/**
 * Shared desktop shell used by Dashboard, Project, History and Account layouts.
 * Provides the outer container, max-width frame and the fixed-width left
 * sidebar column. Caller passes the sidebar content and the right-hand columns
 * (main content, optional middle panel, secondary sidebars) as `children`.
 *
 * Collapse behavior mirrors the Apple app (not Android): collapsing hides the
 * sidebar entirely and navigation moves to the floating bottom bar
 * (`MobileNavigation`), rather than shrinking into a compact icon rail.
 */
export function DesktopShell({
  sidebar,
  children,
  bare = false,
}: DesktopShellProps) {
  const t = useTranslations('navigation');
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);
  const {
    width: sidebarWidth,
    ref: sidebarRef,
    isDragging: isResizingSidebar,
    gripHandlers: sidebarGripHandlers,
  } = useResizableColumn({
    storageKey: SIDEBAR_STORAGE_KEY,
    minWidth: SIDEBAR_WIDTH_EXPANDED,
    maxWidth: SIDEBAR_MAX_WIDTH,
    defaultWidthFor: useCallback(defaultSidebarWidthFor, []),
    edge: 'trailing',
  });
  // Everything that positions itself against the content column reads the
  // sidebar's CURRENT width, not the constant — otherwise a resized sidebar
  // leaves the floating capsule and the stacked drill-down mis-centred.
  const effectiveSidebarWidth = sidebarWidth ?? SIDEBAR_WIDTH_EXPANDED;

  useEffect(() => {
    if (bare) return;
    const root = document.documentElement;
    root.style.setProperty(
      CONTENT_COLUMN_INLINE_START_VAR,
      `${16 + effectiveSidebarWidth + 16}px`
    );
    return () => {
      root.style.removeProperty(CONTENT_COLUMN_INLINE_START_VAR);
    };
  }, [bare, effectiveSidebarWidth]);

  return (
    // Publish the content column's left offset (sidebar width + gap) as a CSS
    // var so viewport-fixed descendants (the floating Generate capsule) can
    // center over the content column instead of the whole viewport. When the
    // sidebar is collapsed the content keeps a small left gutter (for the
    // top-left reveal button), so the offset is that gutter, not 0. The stacked
    // floating footer only exists below xl (< 1728px), so the max-w container
    // adds no side margin there and this px offset is exact.
    //
    // `bare` (< lg) swaps every wrapper to `display: contents` so the boxes
    // produce no layout — the children render exactly as if unwrapped — but the
    // React element tree stays identical to the desktop one, so `children` keep
    // their identity (and state) when the viewport crosses `lg`.
    <div
      className={bare ? 'contents' : 'flex-1 min-h-0 w-full overflow-hidden bg-muted/30'}
      style={
        bare
          ? undefined
          : ({
              '--content-inset-start': collapsed
                ? `${COLLAPSED_LEADING_GUTTER}px`
                : `${effectiveSidebarWidth + 16}px`,
              // Trailing inset matching the collapsed symmetric gutter (see the
              // page scroll containers' `pe-14`). Only the collapsed state has a
              // trailing gutter; with the sidebar expanded the content column
              // reaches the window's trailing edge, so the trailing inset is 0. A
              // viewport-fixed, centered overlay (the floating Generate capsule)
              // pads BOTH sides by these insets to center over the content
              // column instead of the raw viewport.
              '--content-inset-end': collapsed
                ? `${COLLAPSED_LEADING_GUTTER}px`
                : '0px',
            } as CSSProperties)
      }
    >
      <div className={bare ? 'contents' : 'relative h-full w-full'}>
        {/* Sidebar reveal — top-left, wide screens only, shown only while the
            sidebar is collapsed. Mirrors the Apple app, which moved this
            affordance out of the floating bottom bar into the leading top-left
            corner (a standard toolbar position). It sits in the empty margin to
            the left of the page's own leading title; tapping it brings the
            sidebar back. Below `lg` (`bare`) there is no sidebar, so it is
            omitted — the bottom bar is the only navigation there. */}
        {collapsed && !bare && (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={t('toggleSidebar')}
            // Dark glass circle matching the hero ⋮ menu button on the trailing
            // edge, so the two top-corner affordances read as a pair.
            // `top-5` (20px) is not arbitrary: the page's own leading toolbar
            // cluster (back arrow + paging chevrons) is a row of 44px controls
            // starting at the top-bar slot's `pt-4` (16px), so its optical
            // centre sits at 38px. A 36px button centres there at 20px. Keep
            // the two in sync — the button must read as the first item of that
            // same row, not as a floating badge above it.
            className="fixed start-4 top-5 z-40 flex h-9 w-9 items-center justify-center rounded-full bg-background/60 text-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-background/80"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}
        {/* The trailing content column must reach the window's top/trailing/bottom
            edges so its scrollbar pins to the window edge and scrolled content
            clips at the window, not at an inset box. The sidebar keeps its
            floating-card look via its own `my-4 ms-4` margins; `gap-4` still
            separates columns (incl. account's 3-pane).
            When the sidebar is collapsed a small LEADING gutter is reserved so the
            page's own leading content (titles, back chevrons, the concept
            editor's top bar) clears the floating top-left sidebar-reveal button
            instead of sitting under it — mirroring Apple, where that button is a
            leading toolbar item that pushes the title inward rather than overlaps
            it. The gutter is far narrower than the expanded sidebar, so
            collapsing still widens the content. The reserve is LEADING-only on
            purpose: it keeps the scroll column flush to the window's trailing
            edge so the scrollbar pins to the edge. Pages that center their own
            `mx-auto` content add a matching TRAILING padding on their own scroll
            container to re-center, without pulling the scrollbar inward (see
            ProjectDesktopLayout `main`). */}
        <div
          className={
            bare
              ? 'contents'
              : `relative flex h-full gap-4${collapsed ? ' ps-14' : ''}`
          }
        >
          {!collapsed && !bare && (
            // Frosted glass: translucent fill + backdrop-blur so the teal hero
            // wash painted behind the shell shows through softened (iOS Liquid
            // Glass direction). On pages without the wash it reads as a clean
            // light frosted card over the muted background.
            // Same shell-around-a-clipped-column shape as the inspector: the
            // grip must sit outside `overflow-hidden` or it is clipped away and
            // never receives the pointer. It hangs in the gap on the trailing
            // side, so dragging outward widens the nav.
            <div
              ref={sidebarRef}
              className="relative flex-shrink-0 my-4 ms-4"
              style={
                sidebarWidth === null
                  ? { width: SIDEBAR_WIDTH_EXPANDED }
                  : { width: sidebarWidth }
              }
            >
              <aside className="h-full w-full bg-background/60 backdrop-blur-2xl rounded-2xl border border-background shadow-[0_2px_12px_0_rgb(0_0_0_/_0.08)] overflow-hidden flex flex-col">
                {sidebar}
              </aside>
              <div
                aria-hidden
                className={cn(
                  'absolute inset-y-0 end-0 w-0.5 z-[49] pointer-events-none transition-opacity duration-150 bg-primary',
                  isResizingSidebar ? 'opacity-70' : 'opacity-0'
                )}
              />
              <div
                aria-hidden
                {...sidebarGripHandlers}
                className="absolute inset-y-0 end-0 -me-2 w-2 z-[49] cursor-col-resize touch-none"
              />
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
